import { useMsal } from '@azure/msal-react';
import { CommandBar, Dropdown, ICommandBarItemProps, IDropdownOption, Stack, Toggle, useTheme } from '@fluentui/react';
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { RouterParams } from 'App';
import { RootState } from 'app/store';
import { EViewType, styleSummary } from 'global/Application';
import {
    fetch_account_snapshots_Action,
    loadSnapshotAction, updatePharmacyInformationAction
} from "global/Application/dataActions";
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';
import PageLoading from 'global/PageLoading';
import { bcbs_groupconfigurationsnapshot } from 'model/AttributeOptionSets.type';
import * as IXrmTypes from 'model/CRM';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import CustomPanel, { default as CollapsiblePanel } from "../../components/controls/CustomPanel/CustomPanel";
import ControlWrapper, { EControlType } from "../../components/ControlWrapper";
import { useAuthentication } from "../../global/Authentication";
import { AppDataSource } from '../../global/DataSources/AppDataSource';
import {
    setViewType, toggleDisplayAll
} from "../../global/Reducer/statusSlice";
import { formatDate, objectFlip, parseDate } from "../../global/Utility/TSFunctions";
import { IAccountExtended } from "../../model/CRM.type";
import {IBenefit, IBooleanValue, IStringValue} from "../../model/FlexibleAttributeModel";
import { bcbs_fundingtypes } from "../../model/GlobalOptionSetStatic";
import ComparisonAttributeSetGridView from "./ComparisonAttributeSetGridView";
import FlexibleFieldView from "./ComparisonFlexibleFieldView";
import PlanDesign from "./PlanDesign";
import {appInsights} from "../../global/Application/AppInsight";
import EditorNavbar from 'components/layout/EditorNavbar';

interface IProps {
    children: React.ReactNode,
    merge: boolean
}

const PharmacyTool = (props: IProps) => {
    const state = useSelector((state: RootState) => state);
    const dispatch = useDispatch();
    const {id, snapshot1, plan1, snapshot2} = useParams<RouterParams>();
    const bcbs_fundingtypes_byValue = objectFlip(bcbs_fundingtypes);
    const [editOpen, setEditOpen] = useState<boolean>(false);
    const theme = useTheme();
    const history = useHistory();
    const authentication = useAuthentication();
    if (state.status.viewType !== EViewType.ViewOnly) {
        dispatch(setViewType(EViewType.ViewOnly));
    }

    const [selectedLeftSnapshot, setSelectedLeftSnapshot] = React.useState<IDropdownOption<IXrmTypes.bcbs_groupconfigurationsnapshot>>();
    const [selectedRightSnapshot, setSelectedRightSnapshot] = React.useState<IDropdownOption<IXrmTypes.bcbs_groupconfigurationsnapshot>>();

    const {instance} = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    useEffect(() => {
        document.title = `Pharmacy Tool`;
    }, []);

    if (!state)
        return null;

    if (!state.status.snapshotLoading && !state.comp.snapList.find((o) => `${o._bcbs_groupid_value?.toUpperCase()}` === id!?.toUpperCase())) {
        dispatch(fetch_account_snapshots_Action(dataSource, id!));
    }

    if (selectedLeftSnapshot && !state.status.snapshotLoading && state.comp?.leftSnap?.snapshot?.bcbs_groupconfigurationsnapshotid !== selectedLeftSnapshot.data?.bcbs_groupconfigurationsnapshotid) {
        dispatch(loadSnapshotAction(dataSource, id!, { leftSnapshot: selectedLeftSnapshot.data }));
    }


    if (selectedRightSnapshot && !state.status.snapshotLoading && state.comp?.rightSnap?.snapshot?.bcbs_groupconfigurationsnapshotid !== selectedRightSnapshot.data?.bcbs_groupconfigurationsnapshotid) {
        dispatch(loadSnapshotAction(dataSource, id!, { rightSnapshot: selectedRightSnapshot.data }));

    }

    const createdAndEffectiveSort = (b: IXrmTypes.bcbs_groupconfigurationsnapshot, a: IXrmTypes.bcbs_groupconfigurationsnapshot) => {
        let retVal = Date.parse(a.bcbs_effectivedate!) - Date.parse(b.bcbs_effectivedate!);
        if(retVal === 0)
            retVal =  Date.parse(a.createdon!) - Date.parse(b.createdon!);
        return retVal;
    }
    const snapshotOptionsUWRC: IXrmTypes.bcbs_groupconfigurationsnapshot[] = [...state.comp.snapList].filter(q => (q.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value ||
        q.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value ||
        q.statuscode === bcbs_groupconfigurationsnapshot.statuscode['MNIL Underwriting Complete(808900004)'].value ||
        q.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value)).sort(createdAndEffectiveSort);

    const snapshotOptionsInProgress: IXrmTypes.bcbs_groupconfigurationsnapshot[] = [...state.comp.snapList].filter(q => q.statuscode === bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value).sort(createdAndEffectiveSort);

    const snapshotOptionsMergedAndClosed: IXrmTypes.bcbs_groupconfigurationsnapshot[] = [...state.comp.snapList].filter(q => q.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Merged and Closed(808900010)'].value).sort(createdAndEffectiveSort);

    const snapshotOptionsInactive: IXrmTypes.bcbs_groupconfigurationsnapshot[] = [...state.comp.snapList].filter(q => q.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Inactive(808900002)'].value).sort(createdAndEffectiveSort);

    const snapshotOptionsAll: IXrmTypes.bcbs_groupconfigurationsnapshot[] = snapshotOptionsUWRC.concat(snapshotOptionsInProgress, snapshotOptionsMergedAndClosed, snapshotOptionsInactive);

    const snapshotOptionsLeft: IDropdownOption<IXrmTypes.bcbs_groupconfigurationsnapshot>[] = [...snapshotOptionsAll].map((o) => {
        return {
            key: o.bcbs_groupconfigurationsnapshotid!,
            text: `${o.bcbs_name} -- ${bcbs_groupconfigurationsnapshot.statuscode[o.statuscode as bcbs_groupconfigurationsnapshot.Tstatuscode].name}`,
            data: o
        }
    });

    const snapshotOptions: IDropdownOption<IXrmTypes.bcbs_groupconfigurationsnapshot>[] = [...state.comp.snapList].sort(createdAndEffectiveSort).map((o) => {
        return {
            key: o.bcbs_groupconfigurationsnapshotid!,
            text: `${o.bcbs_name} -- ${bcbs_groupconfigurationsnapshot.statuscode[o.statuscode as bcbs_groupconfigurationsnapshot.Tstatuscode].name}`,
            data: o
        }
    });

    if (snapshot2 && snapshotOptionsLeft && !selectedLeftSnapshot && !state.status.snapshotLoading) {
        setSelectedLeftSnapshot(snapshotOptionsLeft.find(o => o.key === snapshot2));
    }

    if (snapshot1 && snapshotOptions && !selectedRightSnapshot && !state.status.snapshotLoading) {
        setSelectedRightSnapshot(snapshotOptions.find(o => o.key === snapshot1));
    }
    const urlLength = state.router.location.pathname.indexOf("/pharmacy/") + 10;

    const onChangeLeft = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption<IXrmTypes.bcbs_groupconfigurationsnapshot>): void => {
        appInsights?.trackEvent({
            name: 'ComparisonOnChangeLeft',
            properties: {
                bcbs_name : item?.data?.bcbs_name,
                bcbs_groupconfigurationsnapshotid: item?.data?.bcbs_groupconfigurationsnapshotid,
            }
        });

        setSelectedLeftSnapshot(item);

        history.push(`${state.router.location.pathname.substr(0, urlLength)}${id}/${selectedRightSnapshot?.key || ""}/${plan1}/${selectedLeftSnapshot?.key || ""}`)
    };
    const rightPlan = state.comp.rightSnap?.json?.plans?.find(p => p.id === plan1);
    const leftPlan = state.comp.leftSnap?.json?.plans?.find(p => p.id === plan1)  ?? state.comp.leftSnap?.json?.plans?.find(p => p.name === rightPlan?.name);
    const medicalCategory = state.data.config.cache?.map_bcbs_config_category_named.Medical;
    const pharmacyCategory = state.data.config.cache?.map_bcbs_config_category_named.Pharmacy;
    const ancillaryCategory = state.data.config.cache?.map_bcbs_config_category_named.Ancillary;
    const leftPharm: IBenefit | undefined = state.comp.leftSnap?.json?.benefits?.find(b => leftPlan?.benefitRefs.find(b2 => b2.id === b.id) && b.bcbs_config_categoryid === pharmacyCategory.bcbs_config_categoryid);
    const rightPharm: IBenefit | undefined = state.comp.rightSnap?.json?.benefits?.find(b => rightPlan?.benefitRefs.find(b2 => b2.id === b.id) && b.bcbs_config_categoryid === pharmacyCategory.bcbs_config_categoryid);
    const leftMedical: IBenefit | undefined = state.comp.leftSnap?.json?.benefits?.find(b => leftPlan?.benefitRefs.find(b2 => b2.id === b.id) && b.bcbs_config_categoryid === medicalCategory.bcbs_config_categoryid);
    const rightMedical: IBenefit | undefined = state.comp.rightSnap?.json?.benefits?.find(b => rightPlan?.benefitRefs.find(b2 => b2.id === b.id) && b.bcbs_config_categoryid === medicalCategory.bcbs_config_categoryid);
    const leftAncillary: IBenefit | undefined = state.comp.leftSnap?.json?.benefits?.find(b => leftPlan?.benefitRefs.find(b2 => b2.id === b.id) && b.bcbs_config_categoryid === ancillaryCategory.bcbs_config_categoryid);
    const rightAncillary: IBenefit | undefined = state.comp.rightSnap?.json?.benefits?.find(b => rightPlan?.benefitRefs.find(b2 => b2.id === b.id) && b.bcbs_config_categoryid === ancillaryCategory.bcbs_config_categoryid);
    const snapLOB = state.comp?.leftSnap?.json?.lineOfBusiness?.value ?? state.comp?.rightSnap?.json?.lineOfBusiness?.value ?? "";
    const snapLOBName = state.data.config?.cache?.options_bcbs_lob.find(s => s.key === snapLOB)?.data?.name;
    const rightCESNumbers = state.comp.rightSnap?.json?.groupStructure?.cesNumbers?.filter(c => state.comp.rightSnap?.json?.groupStructure?.groupNumbers?.filter(g => g.planRef?.id === rightPlan?.id).find(d => c.cesNumber === d.cesNumberRef?.name));
    const leftCESNumbers = state.comp.leftSnap?.json?.groupStructure?.cesNumbers?.filter(c => state.comp.leftSnap?.json?.groupStructure?.groupNumbers?.filter(g => g.planRef?.id === leftPlan?.id).find(d => c.cesNumber === d.cesNumberRef?.name));
    const benefit = rightPharm;
    let carrier = "";
    const fullAccount = state.status?.account as IAccountExtended;
        switch (snapLOBName){
            case "National Alliance":
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Florida")){
                    carrier = "BXN001AFL";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("North Carolina")){
                    carrier = "BXN001ANC";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Louisiana")){
                    carrier = "BXN001ALA";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Kansas City")){
                    carrier = "BXN001AMO";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Kansas")){
                    carrier = "BXN001AKS";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Nebraska")){
                    carrier = "BXN001ANE";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Arizona")){
                    carrier = "BXN001AAZ";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Excellus")){
                    carrier = "BXN001AEX";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Rhode Island")){
                    carrier = "BXN001ARI";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Tennessee")){
                    carrier = "BXN001ATN";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("CareFirst")){
                    carrier = "BXN001ACF";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("HealthNow")){
                    carrier = "BXN001ANY";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Vermont")){
                    carrier = "BXN001AVT";
                    break;
                }
                if(fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Blue Solutions")){
                    carrier = "BXN001ABS";
                    break;
                }
                if (fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]?.includes("Capital")) {
                    carrier = "BXN001ACB";
                    break;
                }
                break;
            case "Major Group":
                if(bcbs_fundingtypes_byValue[state.comp?.leftSnap?.snapshot?.bcbs_fundingtypecode ?? state.comp?.rightSnap?.snapshot?.bcbs_fundingtypecode ?? "missing"] === "Level Funded"){
                    carrier = "BXN001MGL";
                    break;
                }
                else if(bcbs_fundingtypes_byValue[state.comp?.leftSnap?.snapshot?.bcbs_fundingtypecode ?? state.comp?.rightSnap?.snapshot?.bcbs_fundingtypecode ?? "missing"] === "Fully Insured"){
                    carrier = "BXN001MGF";
                    break;
                }
                else{
                    carrier = "BXN001MGA";
                }
                break;
            default:
                carrier = "";
        }

    const headerToggleStyles: any = {
        root: {
            height: '44px',
            marginLeft: '10px',
            marginRight: '10px',
        }
    }

    const savePlanDesign = (line: string) => {
        let temp = state.comp.rightSnap?.json?.pharmacyORE?.find(p => p.id === rightPlan?.id);
        if(temp){
            temp = {...temp, newPharmacyPlanDesign: line};
        }
        else{
            temp = {
                releaseStatus:"In Progress",
                id: plan1!,
                pharmacyPlanDesign: "",
                newPharmacyPlanDesign: line,
                newClientNumber: ""
            }
        }
        dispatch(updatePharmacyInformationAction(dataSource, [temp]));
        setEditOpen(false);

    };
    const saveClientNumDesign = (line: string) => {
        debugger;
        let temp = state.comp.rightSnap?.json?.pharmacyORE?.find(p => p.id === rightPlan?.id);
        if (temp) {
            temp = { ...temp, newClientNumber: line };
        }
        else {
            temp = {
                releaseStatus: "In Progress",
                id: plan1!,
                pharmacyPlanDesign: "",
                newPharmacyPlanDesign: "",
                newClientNumber: line
            }
        }
        dispatch(updatePharmacyInformationAction(dataSource, [temp]));
        setEditOpen(false);

    };
    const highlightStyles = {backgroundColor: 'yellow'};
    
    const generateValue = (benefit: IBenefit | undefined, logicalName: string) => {

        const masterAttribute =  state.data?.config?.masterattributes?.find(c => c.bcbs_logicalname === logicalName);
        const field =  benefit?.fields.find(p => p.masterAttributeID === masterAttribute?.bcbs_config_masterattributeid);
        const fieldValue = field?.values[0];
        return (fieldValue?.value as IBooleanValue | IStringValue)?.value ?? '---';
    }
    const canPharmEdit = !!(authentication.authorizations?.roles['BCBS - Power User'] || authentication.authorizations?.roles['BCBS - Pharmacy']);

    const commandBarItems: ICommandBarItemProps[] = [];

    if (canPharmEdit)
        commandBarItems.push(
            {
                key: 'Pharmacy',
                onRender: () => {
                    return <Toggle
                        label="Ready for Release?"
                        inlineLabel
                        styles={headerToggleStyles}
                        checked={state.comp.rightSnap?.json?.pharmacyORE?.find(p => p.id === rightPlan?.id)?.releaseStatus === "Ready for Release"}
                        onChange={(e, checked) => {
                            let temp = state.comp.rightSnap?.json?.pharmacyORE?.find(p => p.id === rightPlan?.id);
                            if (temp) {
                                temp = { ...temp, releaseStatus: checked ? "Ready for Release" : "In Progress" };
                            }
                            else {
                                temp = {
                                    releaseStatus: checked ? "Ready for Release" : "In Progress",
                                    id: plan1!,
                                    pharmacyPlanDesign: "",
                                    newPharmacyPlanDesign: "",
                                    newClientNumber: ""
                                }
                            }
                            dispatch(updatePharmacyInformationAction(dataSource, [temp]));
                        }}
                    />;
                }
            });

    commandBarItems.push(
        {
            key: 'displayChange',
            onRender: () => {
                return <Toggle
                    label="Show Only Changes?"
                    inlineLabel
                    styles={headerToggleStyles}
                    checked={!state.status.displayAll}
                    onChange={() => dispatch(toggleDisplayAll())}
                />;
            }
        })

    return (
        <>
            <EditorNavbar key={"navbar"} contextMenu={{ key: 'navbar.pharmacyTool', items: commandBarItems }} hideShowDeactivated />
            <div style={{ margin: '48px', marginTop: '25px' }}>
                {!state.status.snapshotLoading
                    ? <>
                        <PlanDesign isOpen={editOpen}
                            dismissPanel={() => {
                                setEditOpen(false);
                            }}
                            onSave={savePlanDesign}
                            onClientNumberUpdate={saveClientNumDesign }
                        />
                        {rightPlan?.deactivated && <h3>This plan is deactivated. Setting this plan ready for release will not release this plan.</h3>}
                        <Stack
                            horizontal
                            horizontalAlign="space-evenly" 
                            verticalAlign="start"
                            tokens={{childrenGap: '15px'}}
                            styles={{root:{margin:'15px 0px 15px 0px'}}}
                        >
                            <Stack.Item grow={1}>
                                <Dropdown
                                    placeholder="Left Snapshot"
                                    label="Left Snapshot"
                                    options={snapshotOptionsLeft}
                                    onChange={onChangeLeft}
                                    selectedKey={selectedLeftSnapshot ? selectedLeftSnapshot.key : undefined}
                                    disabled={false}
                                />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <Dropdown
                                    placeholder="Right Snapshot"
                                    label="Right Snapshot"
                                    options={snapshotOptions}
                                    selectedKey={selectedRightSnapshot ? selectedRightSnapshot.key : undefined}
                                    disabled={true}
                                />
                            </Stack.Item>
                        </Stack>
                        <CollapsiblePanel title={"Summary"} canCollapse={true} defaultCollapsed={false}>
                            <Stack horizontal 
                                horizontalAlign="space-between" 
                                verticalAlign="start"
                            >

                                {state.comp.leftSnap?.snapshot ?
                                    <Stack.Item  grow={1}>
                                        <div style={{ alignSelf: 'end', margin: '0.5em', borderBottom: '1px solid gray' }} >
                                            <span>Snapshot Information</span>
                                        </div>
                                        <dl style={styleSummary.dl}>
                                            <dt style={styleSummary.dt}><span>Name</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap?.snapshot?.bcbs_name !== state.comp.rightSnap?.snapshot?.bcbs_name ? highlightStyles : {}}>{state.comp.leftSnap.snapshot.bcbs_name}</span></dd>

                                        <dt style={styleSummary.dt}><span>Plan Name</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && leftPlan?.name !== rightPlan?.name ? highlightStyles : {}}>{leftPlan?.name}</span></dd>

                                            <dt style={styleSummary.dt}><span>Pharmacy Benefit</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && leftPharm?.name !== rightPharm?.name ? highlightStyles : {}}>{leftPharm?.name}</span></dd>


                                            {state.comp.leftSnap.snapshot.bcbs_effectivedate &&
                                                <>
                                                    <dt style={styleSummary.dt}><span>Effective Date</span></dt>
                                                    <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap?.snapshot?.bcbs_effectivedate !== state.comp.rightSnap?.snapshot?.bcbs_effectivedate ? highlightStyles : {}}>{formatDate(parseDate(state.comp.leftSnap.snapshot.bcbs_effectivedate!, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy')}</span></dd>
                                                </>
                                            }


                                            {state.comp.leftSnap.snapshot.bcbs_fundingtypecode &&
                                                <>
                                                    <dt style={styleSummary.dt}><span>Funding Type</span></dt>
                                                    <dd style={styleSummary.dd}><span style={!state.status.displayAll && (!state.comp.rightSnap.snapshot?.bcbs_fundingtypecode || bcbs_fundingtypes_byValue[state.comp.leftSnap.snapshot.bcbs_fundingtypecode] !== bcbs_fundingtypes_byValue[state.comp.rightSnap.snapshot.bcbs_fundingtypecode])? highlightStyles : {}}>{bcbs_fundingtypes_byValue[state.comp.leftSnap.snapshot.bcbs_fundingtypecode]}</span></dd>
                                                </>
                                            }

                                            <dt style={styleSummary.dt}><span>Line of Business</span></dt>
                                            <dd style={styleSummary.dd}>{snapLOBName}</dd>

                                            <dt style={styleSummary.dt}><span>GF/NGF Type</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && leftPharm?.grandFathered?.name !== rightPharm?.grandFathered?.name ? highlightStyles : {}}>{leftPharm?.grandFathered?.name}</span></dd>

                                            <dt style={styleSummary.dt}><span>ERISA</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap.json?.erisa?.name !== state.comp.rightSnap?.json?.erisa?.name ? highlightStyles : {}}>{state.comp.leftSnap.json?.erisa?.name}</span></dd>
                                            <dt style={styleSummary.dt}>Partner Plan</dt>
                                            <dd style={styleSummary.dd}>{fullAccount && fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]}</dd>

                                            <dt style={styleSummary.dt}><span>Benefit Period Start Date</span></dt>
                                            <dd style={styleSummary.dd}>
                                                <span style={!state.status.displayAll && generateValue(leftMedical, "bcbs_ongoingbenefitperiodruns") !== generateValue(rightMedical, "bcbs_ongoingbenefitperiodruns") ? highlightStyles : {}}>{generateValue(leftMedical, "bcbs_ongoingbenefitperiodruns") }</span></dd>

                                            <dt style={styleSummary.dt}><span>Benefit Period End Date</span></dt>
                                            <dd style={styleSummary.dd}>
                                                <span style={!state.status.displayAll && generateValue(leftMedical, "bcbs_ongoingbenefitperiodrunsthrough1") !== generateValue(rightMedical, "bcbs_ongoingbenefitperiodrunsthrough1") ? highlightStyles : {}}>{generateValue(leftMedical, "bcbs_ongoingbenefitperiodrunsthrough1") }</span></dd>

                                            <dt style={styleSummary.dt}><span>Pharmacy Short Name</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && leftPharm?.name !== rightPharm?.name ? highlightStyles : {}}>{leftPharm?.name}</span></dd>
                                            <dt style={styleSummary.dt}><span>Medical Short Name</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && leftMedical?.name !== rightMedical?.name ? highlightStyles : {}}>{leftMedical?.name}</span></dd>
                                            <dt style={styleSummary.dt}><span>Carrier</span></dt>
                                            <dd style={styleSummary.dd}>{carrier}</dd>
                                            <dt style={styleSummary.dt}><span>Drug Indicator</span></dt>
                                            <dd style={styleSummary.dd}>Y</dd>
                                            <dt style={styleSummary.dt}><span>Client Number(s)</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && JSON.stringify(leftCESNumbers?.map((p, index) => {return `${p.cesNumber}${index + 1 < leftCESNumbers.length? ', ':''}`})) !== JSON.stringify(rightCESNumbers?.map((p, index) => {return `${p.cesNumber}${index + 1 < rightCESNumbers.length? ', ':''}`})) ? highlightStyles : {}}>{leftCESNumbers?.map((p, index) => {return `${p.cesNumber}${index + 1 < leftCESNumbers.length? ', ':''}`})}</span></dd>

                                            <dt style={styleSummary.dt}><span>Manual Client Number(s)</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap.json?.pharmacyORE?.find(p => p.id === leftPlan?.id)?.newClientNumber !== state.comp.rightSnap?.json?.pharmacyORE?.find(p => p.id === rightPlan?.id)?.newClientNumber ? highlightStyles : {}}>{state.comp.leftSnap.json?.pharmacyORE?.find(p => p.id === leftPlan?.id)?.newClientNumber}</span></dd>

                                            <dt style={styleSummary.dt}><span>COB on Pharmacy</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && generateValue(leftMedical, "bcbs_coordinatebenefitsonpharmacy") !== generateValue(rightMedical, "bcbs_coordinatebenefitsonpharmacy") ? highlightStyles : {}}>{generateValue(leftMedical,  "bcbs_coordinatebenefitsonpharmacy")}</span></dd>
                                            <dt style={styleSummary.dt}><span>Pharmacy Plan Design</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap.json?.pharmacyORE?.find(p => p.id === leftPlan?.id)?.pharmacyPlanDesign !== state.comp.rightSnap?.json?.pharmacyORE?.find(p => p.id === rightPlan?.id)?.pharmacyPlanDesign ? highlightStyles : {}}>{state.comp.leftSnap.json?.pharmacyORE?.find(p => p.id === leftPlan?.id)?.pharmacyPlanDesign}</span></dd>
                                            <dt style={styleSummary.dt}><span>New Pharmacy Plan Design</span></dt>
                                            <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap.json?.pharmacyORE?.find(p => p.id === leftPlan?.id)?.newPharmacyPlanDesign !== state.comp.rightSnap?.json?.pharmacyORE?.find(p => p.id === rightPlan?.id)?.newPharmacyPlanDesign ? highlightStyles : {}}>{state.comp.leftSnap.json?.pharmacyORE?.find(p => p.id === leftPlan?.id)?.newPharmacyPlanDesign}</span></dd>


                                        </dl>
                                    </Stack.Item> :
                                    <Stack.Item grow={1}><p>No previous plan to compare with</p></Stack.Item>
                                }
                                {state.comp.rightSnap?.snapshot && 
                                    <Stack.Item grow={1}>

                                        <Stack.Item  grow={1}>
                                            <div style={{ alignSelf: 'end', margin: '0.5em', borderBottom: '1px solid gray' }} >
                                                <span>Snapshot Information</span>
                                            </div>
                                            <dl style={styleSummary.dl}>
                                                <dt style={styleSummary.dt}><span>Name</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap?.snapshot?.bcbs_name !== state.comp.rightSnap?.snapshot?.bcbs_name ? highlightStyles : {}}>{state.comp.rightSnap.snapshot.bcbs_name}</span></dd>
                                                <dt style={styleSummary.dt}><span>Plan Name</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && leftPlan?.name !== rightPlan?.name ? highlightStyles : {}}>{rightPlan?.name}</span></dd>

                                                <dt style={styleSummary.dt}><span>Pharmacy Benefit</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && leftPharm?.name !== rightPharm?.name ? highlightStyles : {}}>{rightPharm?.name}</span></dd>


                                                {state.comp.rightSnap.snapshot.bcbs_effectivedate &&
                                                    <>
                                                        <dt style={styleSummary.dt}><span>Effective Date</span></dt>
                                                        <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap?.snapshot?.bcbs_effectivedate !== state.comp.rightSnap?.snapshot?.bcbs_effectivedate ? highlightStyles : {}}>{formatDate(parseDate(state.comp.rightSnap.snapshot.bcbs_effectivedate!, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy')}</span></dd>
                                                    </>
                                                }


                                                {state.comp.rightSnap.snapshot.bcbs_fundingtypecode &&
                                                    <>
                                                        <dt style={styleSummary.dt}><span>Funding Type</span></dt>
                                                        <dd style={styleSummary.dd}><span style={!state.status.displayAll && (!state.comp.leftSnap?.snapshot?.bcbs_fundingtypecode || bcbs_fundingtypes_byValue[state.comp.leftSnap.snapshot.bcbs_fundingtypecode] !== bcbs_fundingtypes_byValue[state.comp.rightSnap.snapshot.bcbs_fundingtypecode])? highlightStyles : {}}>{bcbs_fundingtypes_byValue[state.comp.rightSnap.snapshot.bcbs_fundingtypecode]}</span></dd>
                                                    </>
                                                }

                                                <dt style={styleSummary.dt}><span>Line of Business</span></dt>
                                                <dd style={styleSummary.dd}>{snapLOBName}</dd>

                                                <dt style={styleSummary.dt}><span>GF/NGF Type</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && leftPharm?.grandFathered?.name !== rightPharm?.grandFathered?.name ? highlightStyles : {}}>{rightPharm?.grandFathered?.name}</span></dd>
                                                <dt style={styleSummary.dt}><span>ERISA</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap.json?.erisa?.name !== state.comp.rightSnap?.json?.erisa?.name ? highlightStyles : {}}>{state.comp.rightSnap.json?.erisa?.name}</span></dd>
                                                <dt style={styleSummary.dt}>Partner Plan</dt>
                                                <dd style={styleSummary.dd}>{fullAccount && fullAccount["_parentaccountid_value@OData.Community.Display.V1.FormattedValue"]}</dd>

                                                <dt style={styleSummary.dt}><span>Benefit Period Start Date</span></dt>
                                                <dd style={styleSummary.dd}>
                                                <span style={!state.status.displayAll && generateValue(leftMedical, "bcbs_ongoingbenefitperiodruns") !== generateValue(rightMedical, "bcbs_ongoingbenefitperiodruns") ? highlightStyles : {}}>
                                                    {generateValue(rightMedical, "bcbs_ongoingbenefitperiodruns") }</span></dd>

                                                <dt style={styleSummary.dt}><span>Benefit Period End Date</span></dt>
                                                <dd style={styleSummary.dd}>
                                                    {generateValue(rightMedical, "bcbs_ongoingbenefitperiodrunsthrough1") }</dd>

                                                <dt style={styleSummary.dt}><span>Pharmacy Short Name</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && leftPharm?.name !== rightPharm?.name ? highlightStyles : {}}>{rightPharm?.name}</span></dd>
                                                <dt style={styleSummary.dt}><span>Medical Short Name</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && leftMedical?.name !== rightMedical?.name ? highlightStyles : {}}>{rightMedical?.name}</span></dd>
                                                <dt style={styleSummary.dt}><span>Carrier</span></dt>
                                                <dd style={styleSummary.dd}>{carrier}</dd>
                                                <dt style={styleSummary.dt}><span>Drug Indicator</span></dt>
                                                <dd style={styleSummary.dd}>Y</dd>
                                                <dt style={styleSummary.dt}><span>Client Number(s)</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && JSON.stringify(leftCESNumbers?.map((p, index) => {return `${p.cesNumber}${index + 1 < leftCESNumbers.length? ', ':''}`})) !== JSON.stringify(rightCESNumbers?.map((p, index) => {return `${p.cesNumber}${index + 1 < rightCESNumbers.length? ', ':''}`})) ? highlightStyles : {}}>{rightCESNumbers?.map((p, index) => {return `${p.cesNumber}${index + 1 < rightCESNumbers.length? ', ':''}`})}</span></dd>

                                                <dt style={styleSummary.dt}><span>Manual Client Number(s)</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap.json?.pharmacyORE?.find(p => p.id === leftPlan?.id)?.newClientNumber !== state.comp.rightSnap?.json?.pharmacyORE?.find(p => p.id === rightPlan?.id)?.newClientNumber ? highlightStyles : {}}>{state.comp.rightSnap.json?.pharmacyORE?.find(p => p.id === rightPlan?.id)?.newClientNumber}</span></dd>

                                                <dt style={styleSummary.dt}><span>COB on Pharmacy</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && generateValue(leftMedical, "bcbs_coordinatebenefitsonpharmacy") !== generateValue(rightMedical, "bcbs_coordinatebenefitsonpharmacy") ? highlightStyles : {}}>{generateValue(rightMedical,  "bcbs_coordinatebenefitsonpharmacy")}</span></dd>
                                                <dt style={styleSummary.dt}><span>Pharmacy Plan Design</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap.json?.pharmacyORE?.find(p => p.id === leftPlan?.id)?.pharmacyPlanDesign !== state.comp.rightSnap?.json?.pharmacyORE?.find(p => p.id === rightPlan?.id)?.pharmacyPlanDesign ? highlightStyles : {}}>{state.comp.rightSnap.json?.pharmacyORE?.find(p => p.id === rightPlan?.id)?.pharmacyPlanDesign}</span></dd>
                                                <dt style={styleSummary.dt}><span>New Pharmacy Plan Design</span></dt>
                                                <dd style={styleSummary.dd}><span style={!state.status.displayAll && state.comp.leftSnap.json?.pharmacyORE?.find(p => p.id === leftPlan?.id)?.newPharmacyPlanDesign !== state.comp.rightSnap?.json?.pharmacyORE?.find(p => p.id === rightPlan?.id)?.newPharmacyPlanDesign ? highlightStyles : {}}>{state.comp.rightSnap.json?.pharmacyORE?.find(p => p.id === rightPlan?.id)?.newPharmacyPlanDesign}</span></dd>
                                            </dl>
                                            <PrimaryButton id="updateplandesign" disabled={!canPharmEdit} text={"Update Fields"} onClick={() => { setEditOpen(true) }} />
                                            

                                        </Stack.Item>
                                    </Stack.Item>

                                }
                            </Stack>
                        </CollapsiblePanel>
                        {benefit && <CollapsiblePanel id={benefit!.id} title={`Benefit: ${benefit!.name}`}
                                                          canCollapse={true}
                                                          defaultCollapsed={!state.status.expandAllPanels}
                    >       <FlexibleFieldView
                                            fields2={benefit!.fields}
                                            category={benefit}
                                            tiers={benefit!.tiers!}
                                            scope={benefit}
                                            benefit={benefit}
                                            disabled
                                            isApproval={false}
                                            compare={!state.status.displayAll}
                                            fields={leftPharm ? state.comp.leftSnap?.json?.benefits?.find((p) => p.id === benefit?.id)?.fields : []}
                                            snap={state.comp.leftSnap}
                                            snap2={state.comp.rightSnap}
                                            leftright={false}
                                            pharmacyORE={true}
                                            leftMedical={leftMedical}
                                            rightMedical={rightMedical}
                                            leftAncillary={leftAncillary}
                                            rightAncillary={rightAncillary}
                                        />
                                </CollapsiblePanel>
                        }
                        <CustomPanel title={"Group Numbers"}
                                     canCollapse={true}
                                     defaultCollapsed={true}
                                     storageId={"plan.groupNumbers"}
                                     contentStyle={{ overflow: 'visible' }}
                        >
                            <div style={{ padding: '8px' }}></div>
                            {(state.comp.leftSnap?.json?.groupStructure?.groupNumbers || state.comp.rightSnap?.json?.groupStructure?.groupNumbers) &&
                                <ComparisonAttributeSetGridView
                                    label={"Group Number"}
                                    category = {state.data.config.cache.map_bcbs_config_category_named["Group Number"].bcbs_config_categoryid as IXrmTypes.bcbs_config_category}
                                    snap={state.comp.leftSnap?.json}
                                    snap2={state.comp.rightSnap?.json}
                                    mergeSnap={state.comp.mergeSnap?.json}
                                    compare={!state.status.displayAll}
                                    leftright={false}
                                    comparison
                                    counter={0}
                                    scope={rightPlan}
                                    merge={false}
                                    tierNames={null}
                                />
                            }
                        </CustomPanel>
                    </>
                    : <PageLoading/>
                }
            </div>
        </>
    );


}

export default PharmacyTool;
